import React, { useState, useEffect } from "react";
import { Switch, Route } from "react-router-dom";
import { QueryLoader, LoadingIndicator } from "@jsq/ds/core";
import { RootProfileContextProvider } from "../../contexts/RootProfileContext";
import {
  AccountTypeEnum,
  useGetComplianceCasesByObjectIdQuery,
} from "../../graphql/supergraph.index";
import { BeneficialOwnersScreen } from "../BeneficialOwnersScreen";
import { PageNotFound } from "../common/PageNotFound";
import { ControllersScreen } from "../ControllersScreen";
import { DocumentCollectorInvestor } from "../documents/DocumentCollectorInvestor";
import { HybridCategorizationPage } from "../HybridCategorizationPage";
import { IndividualLandingScreen } from "../IndividualLandingScreen";
import { IndividualProfileEditScreen } from "../IndividualProfileEditScreen";
import { InstitutionEditScreen } from "../InstitutionEditScreen";
import { JointLandingScreen } from "../JointLandingScreen";
import { OwnerThresholdContainer } from "../OwnerThresholdContainer";
import { ProfileActionOverviewScreen } from "../ProfileActionOverviewScreen";
import { ProfileTree } from "../ProfileTree";
import { SubscriptionComplianceProps, InitialPageSelectorProps } from "./types";

type InitialPage =
  | "individual"
  | "joint"
  | "ira_categorization"
  | "overview"
  | "tree"
  | "documents";

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 3000;

const getInitialPage = (
  accountType: AccountTypeEnum,
  isIraAccount: boolean | null,
  amlEntityTypeId: number | null | undefined,
  ownersCompleted: boolean,
  controllersCompleted: boolean,
  readOnly: boolean,
): InitialPage => {
  if (
    ["individual", "self_directed_ira", "ira_or_non_us"].includes(accountType) ||
    (accountType === "ira" && (isIraAccount === true || readOnly))
  ) {
    return "individual";
  }
  if (["joint", "joint_other", "tic"].includes(accountType)) {
    return "joint";
  }
  if (readOnly) {
    return "documents";
  }
  if (accountType === "ira" && isIraAccount === null) {
    return "ira_categorization";
  }
  const surveyCompleted = amlEntityTypeId !== null;
  if (surveyCompleted && ownersCompleted && controllersCompleted) {
    return "tree";
  } else {
    return "overview";
  }
};

const InitialPageSelector: React.FC<InitialPageSelectorProps> = ({
  caseResult,
  basePath,
  arenaId,
  onStepCompletion,
  readOnly,
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showMissingCase, setShowMissingCase] = useState(false);

  const { data, loading, refetch } = caseResult;
  const complianceCase = data?.complianceCasesByObjectId[0];
  const areOwnersCompleted =
    !!complianceCase?.investingProfile?.caseDataEntryStatus?.ownersCompleted;
  const areControllersCompleted =
    !!complianceCase?.investingProfile?.caseDataEntryStatus?.controllersCompleted;
  const isIra = complianceCase?.investingProfile?.accountType === "ira";
  // Find subscription ID from external objects
  const subscriptionExternalObject = complianceCase?.externalObjects?.find(
    (obj) => obj.objectTypeEnum === "subscription",
  );
  const subscriptionId = subscriptionExternalObject
    ? parseInt(subscriptionExternalObject.objectId)
    : 0;

  // Retry logic for missing compliance case
  useEffect(() => {
    if (!complianceCase && !loading && retryCount < MAX_RETRY_ATTEMPTS && !showMissingCase) {
      if (retryCount === 0) {
        // First attempt failed, start retrying
        setIsRetrying(true);
      }

      const timer = setTimeout(() => {
        setRetryCount((prevCount) => prevCount + 1);
        void refetch?.();
      }, RETRY_DELAY_MS);

      return () => clearTimeout(timer);
    } else if (!complianceCase && retryCount >= MAX_RETRY_ATTEMPTS) {
      // All retries exhausted, show missing case message
      setIsRetrying(false);
      setShowMissingCase(true);
    } else if (complianceCase) {
      // Case found, reset states
      setIsRetrying(false);
      setShowMissingCase(false);
      setRetryCount(0);
    }
  }, [complianceCase, loading, retryCount, showMissingCase, refetch]);

  if (!complianceCase) {
    if (loading || isRetrying) {
      return <LoadingIndicator />;
    } else if (showMissingCase) {
      return <>An update is in progress. Please try reloading this page in a few minutes.</>;
    }
  }

  switch (
    getInitialPage(
      complianceCase?.investingProfile?.accountType as AccountTypeEnum,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
      complianceCase?.investingProfile?.metaData?.is_ira_account ?? null,
      complianceCase?.investingProfile?.amlEntityTypeId,
      areOwnersCompleted,
      areControllersCompleted,
      readOnly,
    )
  ) {
    case "individual":
      return (
        <IndividualLandingScreen
          caseId={complianceCase?.id}
          profileId={complianceCase?.investingProfile?.id}
          showLinkToIraSelector={isIra && !readOnly}
          basePath={basePath}
          arenaId={arenaId}
          subscriptionId={subscriptionId}
          onStepCompletion={onStepCompletion}
          subscriptionInReview={readOnly}
        />
      );
    case "joint":
      return (
        <JointLandingScreen
          caseId={complianceCase?.id}
          onStepCompletion={onStepCompletion}
          arenaId={arenaId}
          subscriptionId={subscriptionId}
          subscriptionInReview={readOnly}
        />
      );
    case "ira_categorization":
      return (
        <HybridCategorizationPage
          caseId={complianceCase?.id?.toString() || ""}
          profileId={complianceCase?.investingProfile?.id}
          basePath={basePath}
          caseResult={caseResult}
        />
      );
    case "overview":
      return (
        <ProfileActionOverviewScreen
          profileId={complianceCase?.investingProfile?.id}
          basePath={basePath}
          caseResult={caseResult}
          caseId={complianceCase?.id?.toString() || ""}
          showLinkToIraSelector={isIra}
        />
      );
    case "tree":
      return (
        <ProfileTree
          caseId={complianceCase?.id?.toString() || ""}
          basePath={basePath}
          arenaId={arenaId}
          subscriptionId={subscriptionId}
          onStepCompletion={onStepCompletion}
        />
      );
    case "documents":
      return (
        <DocumentCollectorInvestor
          caseId={parseInt(complianceCase?.id?.toString() || "0")}
          basePath={basePath}
          onStepCompletion={readOnly ? onStepCompletion : undefined}
        />
      );
    default:
      throw new Error("Error determining initial page.");
  }
};

/**
 * SubscriptionCompliance root component
 *
 * A router component that provides routes to all compliance components
 */
export const SubscriptionCompliance: React.FC<SubscriptionComplianceProps> = ({
  basePath,
  arenaId,
  prospectId,
  onStepCompletion,
  isReadOnly = false,
}) => {
  const result = useGetComplianceCasesByObjectIdQuery({
    variables: {
      arenaId,
      externalObject: {
        objectTypeEnum: "prospect",
        objectId: prospectId.toString(),
        service: "main",
      },
    },
    fetchPolicy: "no-cache",
  });

  const caseId = result.data?.complianceCasesByObjectId[0]?.id?.toString() || "";

  return (
    <QueryLoader result={result}>
      <RootProfileContextProvider caseResult={result}>
        <Switch>
          <Route path={`${basePath}/overview/:profileId`} exact>
            {isReadOnly ? (
              <PageNotFound />
            ) : (
              <ProfileActionOverviewScreen
                basePath={basePath}
                caseId={caseId}
                caseResult={result}
                showBreadcrumb
              />
            )}
          </Route>
          <Route path={basePath} exact>
            <InitialPageSelector
              caseResult={result}
              basePath={basePath}
              arenaId={arenaId}
              onStepCompletion={onStepCompletion}
              readOnly={isReadOnly}
            />
          </Route>
          <Route path={`${basePath}/edit-institution/:profileId`} exact>
            {isReadOnly ? (
              <PageNotFound />
            ) : (
              <InstitutionEditScreen
                caseId={caseId}
                caseResult={result.data}
                basePath={basePath}
              />
            )}
          </Route>
          <Route path={`${basePath}/ira-categorization`} exact>
            {isReadOnly ? (
              <PageNotFound />
            ) : (
              <HybridCategorizationPage
                caseId={caseId}
                caseResult={result}
                basePath={basePath}
                profileId={result.data?.complianceCasesByObjectId[0]?.investingProfile?.id}
              />
            )}
          </Route>
          <Route path={`${basePath}/edit-individual/:profileId`} exact>
            {isReadOnly ? (
              <PageNotFound />
            ) : (
              <IndividualProfileEditScreen caseResult={result.data} basePath={basePath} />
            )}
          </Route>
          <Route path={`${basePath}/owners/threshold-check/:profileId`} exact>
            {isReadOnly ? (
              <PageNotFound />
            ) : (
              <OwnerThresholdContainer
                caseId={caseId}
                basePath={basePath}
                caseResult={result.data}
              />
            )}
          </Route>
          <Route path={`${basePath}/controllers/:profileId`} exact>
            {isReadOnly ? (
              <PageNotFound />
            ) : (
              <ControllersScreen
                caseResult={result.data}
                basePath={basePath}
                arenaId={arenaId}
              />
            )}
          </Route>
          <Route path={`${basePath}/owners/:profileId`} exact>
            {isReadOnly ? (
              <PageNotFound />
            ) : (
              <BeneficialOwnersScreen
                caseResult={result}
                basePath={basePath}
                arenaId={arenaId}
              />
            )}
          </Route>
          <Route path={`${basePath}/documents/:profileId`} exact>
            {isReadOnly ? (
              <PageNotFound />
            ) : (
              <DocumentCollectorInvestor caseId={parseInt(caseId)} basePath={basePath} />
            )}
          </Route>
        </Switch>
      </RootProfileContextProvider>
    </QueryLoader>
  );
};
